import React, { useState, useEffect, useCallback } from "react";
import { useLoading } from "../../../../context/LoadingContext";
import DataTable from "../../../Requirement/components/common/ReusableTable";
import {
  getCurrencyRates,
  convertToBaseCurrency,
  formatData,
  fetchData,
  searchfetchQueries,
  postQuery,
} from "../../../../services/BackendServices";
import SearchFilter from "../../../Requirement/components/common/SearchFilter";
import OfferDetails from "./OfferDetails";
import AlertModal from "../../../Requirement/components/common/AlerModel";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import TrustScoreModal from "../../../../components/common/TrustScoreModal";

const MatchingOffers = ({ requirementDetails, activeUser, onResponse }) => {
  const { setLoading, loading } = useLoading();
  const navigate = useNavigate();
  const [offerList, setOfferList] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [productList, setProductList] = useState([]);
  const [productGradeList, setProductGradeList] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [productDetails, setProductDetails] = useState({});
  const [error, setError] = useState(null);
  const [matchingOffersRequirement, setMatchingOffersRequirement] =
    useState(null);
  const [searchValues, setSearchValues] = useState({
    searchOfferProduct: "",
    searchOfferProductGrade: "",
    searchPriceRange: "",
  });
  const [queryModal, setQueryModal] = useState({
    show: false,
    queryText: "",
  });

  const priceOptions = [
    { value: "", label: "Select Range" },
    { value: "0-1000", label: "0-1000 USD" },
    { value: "1000-2000", label: "1000-2000 USD" },
    { value: "2000-5000", label: "2000-5000 USD" },
    { value: "Above-5000", label: "Above 5000 USD" },
  ];

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  // Load currency rates when component mounts
  useEffect(() => {
    getCurrencyRates();
  }, []);

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  // State for Verification Status Modal
  const [trustModal, setTrustModal] = useState(false);

  const trustModel = useCallback(() => {
    setTrustModal(true);
  }, []);

  const handleQueryModalClose = () => {
    setQueryModal({ show: false, queryText: "" });
  };

  const handleQuerySubmit = async () => {
    if (!queryModal.queryText.trim()) {
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Query text cannot be empty",
        autoClose: false,
      });
      return;
    }

    setLoading(true);
    try {
      // const body = {
      //   target: "sendOfferQuery",
      //   GPOfferQueryInput: {
      //     GPOfferID: selectedOffer.GPOfferID,
      //     QueryText: queryModal.queryText,
      //     GPEmailID: activeUser,
      //     RequirementsID: requirementDetails.RequirementsID,
      //   },
      // };
      // const result = await postQuery(body, "backendops");
      // if (result) {
      //   setModal({
      //     show: true,
      //     type: "success",
      //     title: "Success",
      //     message: "Query sent successfully!",
      //     autoClose: false,
      //     nextAction: () => {
      //       handleQueryModalClose();
      //     },
      //   });
      // }
    } catch (error) {
      console.error("Error sending query:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Failed to send query",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadProductList = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductName",
          rowStart: 0,
          pageSize: 1,
          filterTerms: {
            Status: "ACTIVE",
            ProductID: requirementDetails.ProductID,
          },
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: totalCount,
            filterTerms: {
              Status: "ACTIVE",
              ProductID: requirementDetails.ProductID,
            },
          }),
        ]);

        const distinctProducts = Array.from(
          new Map(allProducts.map((p) => [p.ProductName, p])).values()
        );
        setProductList(distinctProducts);
      } else {
        setProductList([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, requirementDetails]);

  const loadProductGrades = useCallback(
    async (productName) => {
      setLoading(true);
      try {
        const [allGrades] = await Promise.all([
          fetchData({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: 500,
            filterTerms: { ProductName: productName },
          }),
        ]);
        if (allGrades.items.length > 0) {
          setProductGradeList(allGrades.items);
        } else {
          setProductGradeList([]);
        }
      } catch (error) {
        console.error("Error loading product grades: ", error);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const getRequirementProductInfo = useCallback(async (requirementDetails) => {
    try {
      const response = await fetchData({
        target: "listProducts",
        userOrder: "ProductID",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { ProductID: requirementDetails.ProductID },
      });

      setProductDetails(response.items?.[0] || {});
    } catch (error) {
      console.error("Error loading product info:", error);
      setProductDetails([]);
    }
  }, []);

  const fetchMatchingOffers = useCallback(
    async (searchValues, newRowStart = rowStart) => {
      setLoading(true);
      setError(null);
      try {
        let priceFilter = {};
        if (searchValues.searchPriceRange) {
          if (searchValues.searchPriceRange === "Above-5000") {
            priceFilter = { CIFSalePriceGTE: 5000 };
          } else {
            const searchPrice = searchValues.searchPriceRange;
            const [min, max] = searchPrice
              .split("-")
              .map((val) => parseFloat(val));
            priceFilter = {
              CIFSalePriceGTE: min,
              CIFSalePriceLTE: max,
            };
          }
        }

        const quantity = parseFloat(requirementDetails.Quantity) || 0;
        const quantityRange = Math.max(50, quantity * 0.05);
        const destinationCountry =
          requirementDetails.DeliveryLocation?.split("~")[0] || "";
        const today = new Date().toISOString().split("T")[0];
        const body = {
          target: "listGPOfferRequirementDetails",
          userOrder: "CreatedAt DESC",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms: {
            ValidUntilGTE: "'" + today + "'",
            GPOOfferStatus: "Indicative",
            product: productDetails.ProductName,
            grade: productDetails.Grade,
            DestinationCountry: destinationCountry,
            OriginCountry: requirementDetails.OriginCountry || "",
            QuantityGT: quantity - quantityRange,
            QuantityLT: quantity + quantityRange,
            ...priceFilter,
          },
        };

        console.log("Fetching with body:", body);
        const response = await fetchData(body);
        console.log("Fetching with response.items:", response.items);
        if (Array.isArray(response.items) || Array.isArray(response)) {
          setOfferList(response.items || response || []);
          setTotalItems(response.total || 0);
        } else {
          throw new Error("Unexpected response format");
        }
      } catch (error) {
        console.error("Error loading matching offers:", error);
        setError("Failed to load matching offers. Please try again.");
        setOfferList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, productDetails, requirementDetails, setLoading]
  );

  const fields = [
    {
      name: "searchPriceRange",
      label: "Price Range (USD)",
      type: "dropdown",
      options: priceOptions,
      value: searchValues.searchPriceRange,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchPriceRange: value }));
        fetchMatchingOffers({ ...searchValues, searchPriceRange: value }, 0);
      },
    },
  ];

  const handlePageChange = useCallback(
    (page) => {
      const newRowStart = (page - 1) * pageSize;
      console.log("Page changed to:", page, "newRowStart:", newRowStart);
      setRowStart(newRowStart);
      fetchMatchingOffers(searchValues, newRowStart);
    },
    [pageSize, fetchMatchingOffers, searchValues]
  );

  const handleRowClick = useCallback((row) => {
    console.log("Row clicked selected offer:", row);
    setSelectedOffer(row);
    setMatchingOffersRequirement(row);
  }, []);

  const formatPrice = (value) => {
    if (!value || value === "") return "0.00";
    const numValue = parseFloat(value);
    return isNaN(numValue) ? "0.00" : numValue.toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const handleSearch = (searchValues) => {
    setRowStart(0);
    fetchMatchingOffers(searchValues, 0);
  };

  useEffect(() => {
    console.log(requirementDetails, "matchingOfferValue");
    const initializeData = async () => {
      await getRequirementProductInfo(requirementDetails);
    };
    initializeData();
  }, [getRequirementProductInfo, requirementDetails]);

  useEffect(() => {
    console.log(
      "Triggering fetchMatchingOffers with productDetails:",
      productDetails
    );
    if (productDetails && productDetails.ProductName) {
      fetchMatchingOffers(searchValues, rowStart);
    }
  }, [productDetails, searchValues]);

  const Button = ({ label, onClick, disabled = false }) => {
    return (
      <button
        className="btn-action-re"
        onClick={onClick}
        disabled={disabled}
        style={disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}}
      >
        {label}
      </button>
    );
  };

  const sendTrueToParent = () => {
    console.log("child sendTrueParent");
    // onResponse(true); // Uncomment if needed
  };

  const handleShortlist = async (offer) => {
    setLoading(true);
    console.log("handleShortlist triggered");
    try {
      const body = {
        target: "gpShortlistOffer",
        GPOfferExtendedInput: {
          GPOfferID: offer.GPOfferID,
          OfferStatus: "Shortlist",
          Margin: offer.Margin,
          CategoryType: "requirement",
          GPEmailID: activeUser,
          RequirementsID: requirementDetails.RequirementsID,
        },
      };

      console.log("Saving body to API:", body);
      const result = await postQuery(body, "backendops");
      console.log("result@", result);
      if (result) {
        setModal({
          show: true,
          type: "success",
          title: "Success",
          message: `Offer shortlisted successfully!`,
          autoClose: false,
          nextAction: () => {
            sendTrueToParent();
            setSelectedOffer(null);
          },
        });
      }
      await fetchMatchingOffers(searchValues, rowStart);
    } catch (error) {
      console.error("Error saving data:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: `Failed to set shortlist`,
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { id: "OfferNumber", numeric: false, label: "Offer No." },
    { id: "ProductName", numeric: false, label: "Product" },
    { id: "Grade", numeric: false, label: "Grade" },
    {
      id: "Quantity",
      numeric: true,
      label: "Quantity (MT)",
      renderCell: (row) => formatPrice(row.Quantity),
    },
    {
      id: "FOBSalePrice",
      numeric: true,
      label: "FOB Sale Price (USD)",
      renderCell: (row) => row.FOBSalePrice,
    },
    { id: "Country", numeric: false, label: "Origin Country" },
    { id: "DestinationCountry", numeric: false, label: "Destination" },
    { id: "INCO", numeric: false, label: "INCO Terms" },
    {
      id: "GPOOfferStatus",
      numeric: false,
      label: "Offer Type",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";

        switch (row.GPOOfferStatus) {
          case "BAFO":
            bgColor = "#e0f2fe";
            textColor = "#0369a1";
            break;
          case "Firmoffer":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          default:
            bgColor = "#fef3c7";
            textColor = "#b45309";
            row.GPOOfferStatus = "Indicative";
            break;
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {row.GPOOfferStatus}
          </span>
        );
      },
    },
    {
      id: "CreatedAt",
      numeric: false,
      label: "Created Date",
      renderCell: (row) => formatDate(row.CreatedAt),
    },
    {
      id: "ExpiryDate",
      numeric: false,
      label: "Expires",
      renderCell: (row) => formatDate(row.ExpiryDate),
    },
    {
      id: "KYBStatus",
      numeric: false,
      label: "Seller Status",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";

        switch (row.KYBStatus) {
          case "Approved":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          default:
            bgColor = "#fef3c7";
            textColor = "#b45309";
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
              display: "inline-block",
            }}
          >
            {row.KYBStatus === "Approved" ? "Verified" : "Unverified"}
          </span>
        );
      },
    },
  ];
  const handleRaiseRFQ = async (offer) => {
    console.log("handleRaiseRFQ triggered", requirementDetails);
    console.log("raiseRfqOffer::", offer);
    setModal({
      show: true,
      type: "warning",
      title: "Confirm Submission",
      message: "Are you sure you want to create trade RFQ of this offer?",
      isConfirm: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Skip",
      onConfirm: async () => {
        console.log("Confirm chosen...");
        console.log("#productDetails:", productDetails);
        let insertedRequirementsID = null;
        let insertedRequirementsNo = null;
        const insertedPaymentConditionsIDs = [];
        // setSubmitted(true);
        setLoading(true);

        try {
          let totalTrfqItems = 0;
          try {
            const bodyTradeRfq = {
              target: "listRequirements",
              userOrder: "RequirementsID",
              rowStart: 0,
              pageSize: 1,
              filterTerms: {
                CategoryType: "rfq",
              },
            };
            const responseTradeRfq = await fetchData(bodyTradeRfq);
            totalTrfqItems = responseTradeRfq.total || 0;
          } catch (error) {
            console.error("Error loading RFQs: ", error);
            totalTrfqItems = 0;
          }

          const packageingValue = {
            item: offer.PackagingType,
            size: offer.PackagingSize,
          };
          const curreDate = new Date();
          const reqNoDate = dayjs(curreDate).format("YYYYMMDD");
          const reqCount = totalTrfqItems + 1;
          const reqNumber = reqNoDate + reqCount;

          console.log("@@@", packageingValue, reqNumber);

          const requestedQuantityValue = parseInt(offer.Quantity || 0);
          const requestedProductID = offer.ProductID || "";
          const categoryValue = productDetails.ProductCategoryID || "";
          const productType = "";
          const priceOfferedValue =
            convertToBaseCurrency(offer.Price, offer.Currency || "INR") ||
            "0.00";
          const deliveryPeriodTillValue = offer.ExpiryDate || "";
          const deliveryPeriodFromValue =
            dayjs(curreDate).format("YYYY-MM-DD") || "";
          const validUntilValue = offer.ExpiryDate || "";
          const incotermValue = offer.INCO || "";
          const destinationCountry = offer.DestinationCountry || "";
          const deliveryPortValue = offer.DischargePort || "";
          const locationsValue = destinationCountry + "~" + deliveryPortValue;
          const originCountryValue = offer.Country || offer.OriginCountry || "";

          let gpEmail = offer.GPEmailID || "";

          let recordStatus = "Draft";

          const selectedDocsString = " ";

          //   //insert
          const body = {
            target: "createRequirements",
            ExtraInfo: {
              ProductName: productDetails?.ProductName,
              Grade: productDetails?.Grade,
            },
            requirementsInput: {
              RequirementsID: uuidv4(),
              RequirementNumber: reqNumber,
              GPEmailID: activeUser,
              BuyerEmailID: " ",
              CreatedByEmailID: activeUser,
              ProductID: requestedProductID,
              Quantity: requestedQuantityValue,
              Price: priceOfferedValue,
              DeliveryDate: deliveryPeriodTillValue,
              RequirementDate: dayjs(curreDate).format("YYYY-MM-DD"),
              MarketPlacement: "No",
              ValidUntil: validUntilValue,
              Incoterm: incotermValue,
              DeliveryLocation: locationsValue,
              DeliveryPeriodFrom: deliveryPeriodFromValue,
              DeliveryPeriodTill: deliveryPeriodTillValue,
              OriginCountry: originCountryValue,
              ProductPackaging: packageingValue,
              RequestedQuantity: requestedQuantityValue,
              PriceOffered: priceOfferedValue,
              Status: recordStatus,
              CategoryType: "rfq",
              Deleted: 0,
              Title: "RFQ generated from " + offer.OfferNumber || " ",
              CreateDate: dayjs(curreDate).format("YYYY-MM-DD 00:00:00"),
              SubmitedDate: dayjs(curreDate).format("YYYY-MM-DD 00:00:00"),
              HsCode: offer.HsCode || "",
              Variety: offer.Variety || "",
              ProductSpecifications: offer.productSpecifications || " ",
              AdditionalProductNotes: "",
              AdditionalPackagingNotes: offer.AdditionalPackagingNotes || " ",
              ShippingMethodPreference: offer.ShippingMethodPreference || " ",
              PartialDeliveryAllowed: offer.PartialDeliveryAllowed || 0,
              AdditionalLogisticsNotes: offer.TransitTime || " ",
              UnloadingResponsibility: offer.UnloadingResponsibility || "",
              TransshipmentAllowed: 0,
              PreferredPaymentMethod: offer.PreferredPaymentMethod || "",
              Currency: offer.Currency || "",
              PaymentTimeline: offer.PaymentTerms || " ",
              SpecialInstructionsSuppliers:
                offer.SpecialInstructionsBuyer || " ",
              QualityInspectionRequired: 0,
              InternalComments: offer.Internalcomments || " ",
              RequiredSupplierCertifications: 0,
              ComparisonCriteriaPriority: " ",
              RequiredDocuments: selectedDocsString,
            },
          };

          const result = await postQuery(body);
          console.log(result);
          if (!result || !result.items)
            throw new Error("Failed to create RFQ.");

          insertedRequirementsID = result.items.RequirementsID;
          insertedRequirementsNo = result.items.RequirementNumber;
          console.log(insertedRequirementsID);
          const bodyProductInfo = {
            target: "createRequirementProductInfo",
            requirementProductInfoInput: {
              RequirementProductInfoID: uuidv4(),
              RequirementsID: insertedRequirementsID,
              Category: categoryValue,
              ProductID: requestedProductID,
              ProductTypeID: productType,
              Deleted: 0,
            },
          };
          await postQuery(bodyProductInfo);

          const bodyRTH = {
            target: "createRequirementTxnHistory",
            requirementTxnHistoryInput: {
              RequirementTxnHistoryID: uuidv4(),
              RequirementsID: insertedRequirementsID,
              ActionedByEmail: activeUser,
              Status: recordStatus,
              Deleted: 0,
            },
          };
          await postQuery(bodyRTH);

          setModal({
            show: true,
            type: "success",
            title: "Success!",
            message: `${insertedRequirementsNo} created successfully.`,
            autoClose: false,
            nextAction: () => {
              navigate("/global-partner/requirement");
              // setIsFormVisible(false);
              // searchRFQs(searchValues);
            },
          });
        } catch (error) {
          console.error("Submission error:", error);
          try {
            await Promise.all(
              insertedPaymentConditionsIDs.map(async (paymentConditionsID) => {
                const bodyDelPC = {
                  target: "deletePaymentConditions",
                  PaymentConditionsID: paymentConditionsID,
                };
                await postQuery(bodyDelPC);
              })
            );
            if (insertedRequirementsID) {
              const bodyDelR = {
                target: "deleteRequirements",
                RequirementsID: insertedRequirementsID,
              };
              await postQuery(bodyDelR);
            }
          } catch (rollbackError) {
            console.error("Rollback error:", rollbackError);
            setModal({
              show: true,
              type: "error",
              title: "Rollback Error!",
              message:
                "Failed to clean up data after error. Please contact support.",
              autoClose: false,
            });
            setLoading(false);
            // setSubmitted(false);
            return;
          }

          setModal({
            show: true,
            type: "error",
            title: "Error!",
            message: error.message || "Failed to submit RFQ.",
            autoClose: false,
          });
        } finally {
          setLoading(false);
          //   setSubmitted(false);
        }
      },
      onCancel: async () => {
        // If not confirmed, proceed with original else block
        console.log("Cancel chosen...");
        handleModalClose();
      },
    });
  };
  const currentPage = Math.floor(rowStart / pageSize) + 1;
  const selectedRows = matchingOffersRequirement
    ? [
        matchingOffersRequirement.GPOfferID ||
          matchingOffersRequirement.GPOfferID,
      ]
    : [];

  return (
    <div className="matching-offers-container" style={{ marginTop: "20px" }}>
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        isConfirm={modal.isConfirm}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      {queryModal.show && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "8px",
              width: "500px",
              maxWidth: "90%",
            }}
          >
            <h4 style={{ marginBottom: "20px" }}>Send Query</h4>
            <textarea
              value={queryModal.queryText}
              onChange={(e) =>
                setQueryModal({ ...queryModal, queryText: e.target.value })
              }
              placeholder="Enter your query here..."
              style={{
                width: "100%",
                minHeight: "100px",
                marginBottom: "20px",
                padding: "10px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <Button label="Cancel" onClick={handleQueryModalClose} />
              <Button
                label="Submit"
                onClick={handleQuerySubmit}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      )}
      <div className="search-filter4">
        <div className="title4">
          <div className="head-text">Matching Offers</div>
        </div>
        <div className="filter4">
          <SearchFilter fields={fields} onSearch={handleSearch} />
        </div>
      </div>
      {loading && <div className="loading">Loading offers...</div>}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && offerList.length === 0 && (
        <div className="empty-state">No matching offers found.</div>
      )}
      {!loading && offerList.length > 0 && (
        <DataTable
          columns={columns}
          data={offerList}
          total={totalItems}
          title="Matching Offers Information"
          rowsPerPage={pageSize}
          onPageChange={handlePageChange}
          onRowClick={handleRowClick}
          currentPage={currentPage}
          selectedRows={selectedRows}
        />
      )}
      {selectedOffer && (
        <div className="offer-detail1">
          <div className="header-text-re">
            <div className="head-text">Offer Details</div>
            {selectedOffer ? (
              selectedOffer.GPOExtendedStatus === "Shortlist" ? (
                <>
                  <Button
                    className="width-170"
                    label="Verification Status"
                    onClick={trustModel}
                    style={{ width: "auto" }}
                  />
                  <div className="submit-btn-more">Shortlisted</div>
                </>
              ) : (
                <div className="button-group">
                  <Button label="Verification Status" onClick={trustModel} />
                  <Button
                    label="Shortlist"
                    onClick={() => handleShortlist(selectedOffer)}
                  />
                  <Button
                    label="Raise RFQ"
                    onClick={() => handleRaiseRFQ(selectedOffer)}
                  />
                  {/*  <Button
                    label="Send Query"
                    onClick={() => setQueryModal({ show: true, queryText: "" })}
                  /> */}
                </div>
              )
            ) : (
              <div></div>
            )}
          </div>

          <OfferDetails
            offer={selectedOffer}
            btnShortlist={false}
            btnSendQuery={false}
            btnRequestQuote={false}
            btnRequestSpecifications={false}
            btnRequestBestPrice={false}
            btnNegotiate={false}
            btnRequestShippingDetails={false}
            btnLogisticsSupport={false}
            btnRaiseQuery={true}
          />
        </div>
      )}
      {/* Verification Status Modal */}
      {trustModal && selectedOffer && (
        <TrustScoreModal
          onClose={() => setTrustModal(false)}
          userEmail={selectedOffer.SellerEmailID}
        />
      )}
    </div>
  );
};

export default MatchingOffers;
